import { Link } from 'react-router-dom';
import { Plus, Layers, Wand2, Calendar, CalendarDays, ChevronRight, Sun } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import type { Fit, Wear } from '@/lib/types';

function monthRange(): { from: string; to: string } {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const fmt = (d: Date) => d.toISOString().split('T')[0];
  return { from: fmt(first), to: fmt(last) };
}

export function FitsHubPage() {
  const { data: fitsData } = useApi<{ fits: Fit[] }>('/fits');
  const { from, to } = monthRange();
  const { data: wearsData } = useApi<{ wears: Wear[] }>(`/wears?from=${from}&to=${to}`);

  const totalFits = fitsData?.fits.length;
  const thisMonth = wearsData?.wears.filter((w) => w.kind === 'fit').length;

  const options = [
    { icon: Sun, title: 'What to Wear Today', description: 'AI picks an outfit for today’s weather', href: '/fits/today', accent: true },
    { icon: Plus, title: 'Add a Fit', description: 'Log a new outfit you wore', href: '/fits/add' },
    { icon: Layers, title: 'Available Fits', description: 'Browse your saved outfit combinations', href: '/fits/available', count: totalFits },
    { icon: Wand2, title: 'Fit Builder', description: 'Build outfits by hand or with the AI stylist', href: '/fits/builder/sessions' },
    { icon: Calendar, title: 'Fit Timeline', description: 'View your outfit history by date', href: '/fits/timeline' },
    { icon: CalendarDays, title: 'Fit Planner', description: 'Plan outfits for upcoming trips and events', href: '/fits/planner' },
  ];

  return (
    <Page>
      <PageHeader section="Fits" />
      <Main className="space-y-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Fits</p>
              <p className="text-xl font-bold text-foreground mt-1">{totalFits ?? '—'}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">This Month</p>
              <p className="text-xl font-bold text-foreground mt-1">{thisMonth ?? '—'}</p>
            </div>
          </div>
        </Card>

        <div className="space-y-3">
          {options.map((option) => (
            <Link
              key={option.title}
              to={option.href}
              className={`flex items-center gap-4 rounded-xl p-4 border transition-all group ${
                option.accent
                  ? 'bg-primary text-primary-foreground border-primary hover:opacity-90'
                  : 'bg-card border-border hover:border-primary/30'
              }`}
            >
              <div
                className={`p-3 rounded-xl transition-colors ${
                  option.accent ? 'bg-primary-foreground/10' : 'bg-secondary group-hover:bg-accent/20'
                }`}
              >
                <option.icon
                  className={`w-5 h-5 transition-colors ${
                    option.accent ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-primary'
                  }`}
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className={`text-sm font-semibold ${option.accent ? 'text-primary-foreground' : 'text-foreground'}`}>
                    {option.title}
                  </h3>
                  {option.count !== undefined && (
                    <span className="px-2 py-0.5 bg-secondary rounded-full text-xs font-medium text-muted-foreground">
                      {option.count}
                    </span>
                  )}
                </div>
                <p className={`text-xs mt-0.5 ${option.accent ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                  {option.description}
                </p>
              </div>
              <ChevronRight
                className={`w-5 h-5 transition-colors ${
                  option.accent ? 'text-primary-foreground/70' : 'text-muted-foreground group-hover:text-primary'
                }`}
              />
            </Link>
          ))}
        </div>
      </Main>
    </Page>
  );
}
